import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import api from '../services/api';
import { toast } from 'react-toastify';
import './Reports.css';

// Función para traducir estados de asistencia
const translateAttendanceStatus = (status) => {
  const translations = {
    'present': 'Presente',
    'absent': 'Ausente',
    'late': 'Tardío',
    'excused': 'Justificado'
  };
  return translations[status] || status;
};

const ReportAttendances = () => {
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    groupId: ''
  });
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { data: groups } = useQuery('groups', async () => {
    const response = await api.get('/groups', { params: { is_active: true } });
    return response.data || [];
  });

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      if (filters.groupId) params.group_id = filters.groupId;

      const response = await api.get('/attendances', { params });
      setPreviewData(response.data || []);
    } catch (error) {
      console.error('Error al cargar vista previa:', error);
      toast.error('Error al cargar vista previa');
      setPreviewData([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadPreview();
    setCurrentPage(1);
  }, [loadPreview]);

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams();
      
      if (filters.dateFrom) queryParams.append('date_from', filters.dateFrom);
      if (filters.dateTo) queryParams.append('date_to', filters.dateTo);
      if (filters.groupId) queryParams.append('group_id', filters.groupId);
      
      const baseUrl = api.defaults.baseURL || 'http://localhost:8000/api/v1';
      const url = `${baseUrl}/reports/attendances/excel${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al descargar el reporte');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `asistencias_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Reporte descargado correctamente');
    } catch (error) {
      console.error('Error al descargar:', error);
      toast.error('Error al descargar el reporte');
    }
  };

  return (
    <div className="reports-page">
      <div className="page-header">
        <h2>✅ Reporte de Asistencias</h2>
        <p>Visualiza y exporta el registro de asistencias</p>
      </div>

      {/* Filtros */}
      <div className="card filters-section">
        <h3>Filtros (Opcionales)</h3>
        <div className="filters-grid">
          <div className="form-group">
            <label>Fecha Desde</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Fecha Hasta</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Grupo</label>
            <select
              value={filters.groupId}
              onChange={(e) => setFilters({ ...filters, groupId: e.target.value })}
            >
              <option value="">Todos los grupos</option>
              {groups?.map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Vista previa */}
      <div className="card preview-section">
        <div className="preview-header">
          <h3>Vista Previa</h3>
          <div className="export-buttons">
            <button 
              className="btn btn-primary"
              onClick={handleDownload}
            >
              📊 Exportar a Excel
            </button>
          </div>
        </div>

        {loading ? (
          <div className="preview-loading">
            <p>Cargando vista previa...</p>
          </div>
        ) : !previewData || previewData.length === 0 ? (
          <div className="preview-empty">
            <p>No hay datos para mostrar con los filtros seleccionados</p>
          </div>
        ) : (
          <div className="preview-table-container">
            <table className="preview-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Estudiante</th>
                  <th>Grupo</th>
                  <th>Fecha</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {previewData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(attendance => (
                  <tr key={attendance.id}>
                    <td>{attendance.id}</td>
                    <td>{attendance.student?.full_name || 'Sin estudiante'}</td>
                    <td>{attendance.group?.name || 'Sin grupo'}</td>
                    <td>{attendance.attendance_date ? new Date(attendance.attendance_date).toLocaleDateString() : ''}</td>
                    <td>
                      <span className={`status-badge status-${attendance.status}`}>
                        {translateAttendanceStatus(attendance.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {previewData.length > 0 && (
              <div className="pagination-container">
                <div className="pagination-info">
                  Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, previewData.length)} - {Math.min(currentPage * itemsPerPage, previewData.length)} de {previewData.length} registros
                </div>
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    « Primera
                  </button>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ‹ Anterior
                  </button>
                  <span className="pagination-current">
                    Página {currentPage} de {Math.ceil(previewData.length / itemsPerPage)}
                  </span>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(previewData.length / itemsPerPage)))}
                    disabled={currentPage >= Math.ceil(previewData.length / itemsPerPage)}
                  >
                    Siguiente ›
                  </button>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(Math.ceil(previewData.length / itemsPerPage))}
                    disabled={currentPage >= Math.ceil(previewData.length / itemsPerPage)}
                  >
                    Última »
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportAttendances;
